import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getPinnedItemStates,
  listPinnedItems,
  pinItem as pinItemRequest,
  unpinItem as unpinItemRequest,
} from "../api/pinnedItems.api";
import { getPinnedItemModuleLabel } from "../features/pins/pinnedItemRoutes";

const DEFAULT_SCOPE_KEY = "self";

const listeners = new Set();
const store = {
  scopes: {
    [DEFAULT_SCOPE_KEY]: {
      items: [],
      loaded: false,
      loading: false,
      error: null,
    },
  },
  states: {},
  statesLoadingKeys: new Set(),
  statesErrorKeys: {},
};

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const makePinnedItemKey = (itemType, itemId) => `${itemType}:${itemId}`;

const getScopeKey = (viewUserId) => {
  if (viewUserId === null || viewUserId === undefined || viewUserId === "") {
    return DEFAULT_SCOPE_KEY;
  }
  return `user:${String(viewUserId)}`;
};

const ensureScope = (scopeKey) => {
  if (!store.scopes[scopeKey]) {
    store.scopes[scopeKey] = {
      items: [],
      loaded: false,
      loading: false,
      error: null,
    };
  }
  return store.scopes[scopeKey];
};

const setPinnedState = (itemType, itemId, value) => {
  store.states[makePinnedItemKey(itemType, itemId)] = Boolean(value);
};

const readStoreSnapshot = (scopeKey = DEFAULT_SCOPE_KEY) => ({
  ...ensureScope(scopeKey),
  states: store.states,
  statesLoadingKeys: store.statesLoadingKeys,
  statesErrorKeys: store.statesErrorKeys,
});

const normalizeOptimisticItem = ({ itemType, itemId, summary = {}, pinnedAt = new Date().toISOString() }) => ({
  pin_id: `optimistic:${itemType}:${itemId}`,
  item_type: itemType,
  item_id: String(itemId),
  module_label: summary.moduleLabel || getPinnedItemModuleLabel(itemType),
  title: summary.title || "Pinned item",
  secondary_text: summary.secondaryText || null,
  status: summary.status || null,
  owner_display: summary.ownerDisplay || null,
  updated_at: summary.updatedAt || null,
  pinned_at: pinnedAt,
});

const setItems = (scopeKey, items) => {
  const scope = ensureScope(scopeKey);
  scope.items = Array.isArray(items) ? items : [];
  scope.loaded = true;
  scope.loading = false;
  scope.error = null;
  if (scopeKey === DEFAULT_SCOPE_KEY) {
    scope.items.forEach((item) => setPinnedState(item.item_type, item.item_id, true));
  }
  emitChange();
};

const updateItems = (scopeKey, updater) => {
  const scope = ensureScope(scopeKey);
  const nextItems = typeof updater === "function" ? updater(scope.items) : updater;
  scope.items = Array.isArray(nextItems) ? nextItems : [];
  emitChange();
};

export const reloadPinnedItems = async ({ viewUserId } = {}) => {
  const scopeKey = getScopeKey(viewUserId);
  const scope = ensureScope(scopeKey);
  if (scope.loading) {
    return scope.items;
  }
  scope.loading = true;
  scope.error = null;
  emitChange();
  try {
    const items = await listPinnedItems({ viewUserId });
    setItems(scopeKey, items);
    return items;
  } catch (error) {
    scope.loading = false;
    scope.error = error;
    emitChange();
    throw error;
  }
};

const ensurePinnedItemsLoaded = async ({ viewUserId } = {}) => {
  const scopeKey = getScopeKey(viewUserId);
  const scope = ensureScope(scopeKey);
  if (scope.loaded) {
    return scope.items;
  }
  return reloadPinnedItems({ viewUserId });
};

const markStatesLoading = (items, value) => {
  items.forEach(({ itemType, itemId }) => {
    const key = makePinnedItemKey(itemType, itemId);
    if (value) {
      store.statesLoadingKeys.add(key);
    } else {
      store.statesLoadingKeys.delete(key);
    }
  });
};

export const lookupPinnedItemStates = async (items) => {
  const pending = items.filter(({ itemType, itemId }) => {
    const key = makePinnedItemKey(itemType, itemId);
    return store.states[key] === undefined && !store.statesLoadingKeys.has(key);
  });
  if (pending.length === 0) {
    return store.states;
  }
  markStatesLoading(pending, true);
  emitChange();
  try {
    const states = await getPinnedItemStates(pending);
    Object.entries(states).forEach(([key, value]) => {
      store.states[key] = Boolean(value);
      delete store.statesErrorKeys[key];
    });
    return store.states;
  } catch (error) {
    pending.forEach(({ itemType, itemId }) => {
      store.statesErrorKeys[makePinnedItemKey(itemType, itemId)] = error;
    });
    emitChange();
    throw error;
  } finally {
    markStatesLoading(pending, false);
    emitChange();
  }
};

const applyOptimisticPin = ({ itemType, itemId, summary }) => {
  setPinnedState(itemType, itemId, true);
  const selfScope = ensureScope(DEFAULT_SCOPE_KEY);
  if (selfScope.loaded) {
    updateItems(DEFAULT_SCOPE_KEY, (current) => {
      const existing = current.find(
        (item) => item.item_type === itemType && String(item.item_id) === String(itemId)
      );
      if (existing) {
        return current;
      }
      return [normalizeOptimisticItem({ itemType, itemId, summary }), ...current];
    });
  } else {
    emitChange();
  }
};

const applyOptimisticUnpin = ({ itemType, itemId }) => {
  setPinnedState(itemType, itemId, false);
  const selfScope = ensureScope(DEFAULT_SCOPE_KEY);
  if (selfScope.loaded) {
    updateItems(DEFAULT_SCOPE_KEY, (current) =>
      current.filter(
        (item) => !(item.item_type === itemType && String(item.item_id) === String(itemId))
      )
    );
  } else {
    emitChange();
  }
};

export const togglePinnedItem = async ({ itemType, itemId, isPinned, summary }) => {
  const normalizedId = String(itemId);
  const previousItems = ensureScope(DEFAULT_SCOPE_KEY).items;
  const previousState = store.states[makePinnedItemKey(itemType, normalizedId)];

  if (isPinned) {
    applyOptimisticUnpin({ itemType, itemId: normalizedId });
    try {
      await unpinItemRequest({ itemType, itemId: normalizedId });
      return false;
    } catch (error) {
      ensureScope(DEFAULT_SCOPE_KEY).items = previousItems;
      store.states[makePinnedItemKey(itemType, normalizedId)] = previousState;
      emitChange();
      throw error;
    }
  }

  applyOptimisticPin({ itemType, itemId: normalizedId, summary });
  try {
    await pinItemRequest({ itemType, itemId: normalizedId });
    return true;
  } catch (error) {
    ensureScope(DEFAULT_SCOPE_KEY).items = previousItems;
    store.states[makePinnedItemKey(itemType, normalizedId)] = previousState;
    emitChange();
    throw error;
  }
};

export const usePinnedItems = ({ viewUserId } = {}) => {
  const scopeKey = getScopeKey(viewUserId);
  const [, setVersion] = useState(0);

  useEffect(() => subscribe(() => setVersion((value) => value + 1)), []);

  useEffect(() => {
    ensurePinnedItemsLoaded({ viewUserId }).catch(() => {});
  }, [viewUserId]);

  const handleUnpin = useCallback(
    async ({ itemType, itemId }) =>
      togglePinnedItem({
        itemType,
        itemId,
        isPinned: true,
      }),
    []
  );

  const scope = ensureScope(scopeKey);

  return {
    items: scope.items,
    isLoading: scope.loading && !scope.loaded,
    isRefreshing: scope.loading && scope.loaded,
    error: scope.error,
    reload: () => reloadPinnedItems({ viewUserId }),
    unpinItem: handleUnpin,
  };
};

export const usePinnedItem = ({ itemType, itemId, summary }) => {
  const normalizedId = String(itemId);
  const key = makePinnedItemKey(itemType, normalizedId);
  const [, setVersion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => subscribe(() => setVersion((value) => value + 1)), []);

  useEffect(() => {
    lookupPinnedItemStates([{ itemType, itemId: normalizedId }]).catch(setError);
  }, [itemType, normalizedId]);

  const isPinned = Boolean(store.states[key]);

  const toggle = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const nextState = await togglePinnedItem({
        itemType,
        itemId: normalizedId,
        isPinned,
        summary,
      });
      return nextState;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    } finally {
      setIsSubmitting(false);
    }
  }, [isPinned, itemType, normalizedId, summary]);

  return {
    isPinned,
    isLoading: store.statesLoadingKeys.has(key),
    isSubmitting,
    error,
    toggle,
  };
};

export const usePinnedItemStates = (items) => {
  const normalizedItems = useMemo(
    () =>
      items.map((item) => ({
        itemType: item.itemType,
        itemId: String(item.itemId),
      })),
    [items]
  );
  const [, setVersion] = useState(0);

  useEffect(() => subscribe(() => setVersion((value) => value + 1)), []);

  useEffect(() => {
    if (normalizedItems.length === 0) {
      return undefined;
    }
    lookupPinnedItemStates(normalizedItems).catch(() => {});
    return undefined;
  }, [normalizedItems]);

  return normalizedItems.reduce((acc, item) => {
    acc[makePinnedItemKey(item.itemType, item.itemId)] = Boolean(
      store.states[makePinnedItemKey(item.itemType, item.itemId)]
    );
    return acc;
  }, {});
};

export const __pinnedItemsStoreForTests = {
  readStoreSnapshot,
  prime({ items = [], states = {}, viewUserId = null } = {}) {
    const scope = ensureScope(getScopeKey(viewUserId));
    scope.items = items;
    scope.loaded = true;
    scope.loading = false;
    scope.error = null;
    store.states = { ...states };
    store.statesLoadingKeys = new Set();
    store.statesErrorKeys = {};
    emitChange();
  },
  reset() {
    store.scopes = {
      [DEFAULT_SCOPE_KEY]: {
        items: [],
        loaded: false,
        loading: false,
        error: null,
      },
    };
    store.states = {};
    store.statesLoadingKeys = new Set();
    store.statesErrorKeys = {};
    emitChange();
  },
};
